import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import {
  LayoutDashboard, Tag, TrendingUp, Calendar, CheckSquare, MessageCircle,
  Package, ShoppingCart, BarChart2, Calculator, ShoppingBag, CreditCard,
  Flame, ArrowLeftRight, Wrench, Database, RefreshCw, DollarSign,
  Banknote, Truck, Clock, AlertCircle, FileText, RotateCcw,
  Users, BookOpen, Phone, Sparkles, FileSpreadsheet, SearchX,
  Scale, ArrowUpDown, History, Moon, Download, UserCheck,
  CalendarDays, Settings, Shield, Megaphone, LogOut,
  BellOff, X, Menu,
} from 'lucide-react';
import SubscriptionPaused from './SubscriptionPaused';

/** Fetches alert counts (overdue followups). Polls every 5 min. */
function useAlerts(role) {
  const [alerts, setAlerts] = useState({ overdue: 0, dueToday: 0 });

  useEffect(() => {
    if (role !== 'admin') return;

    async function fetchAlerts() {
      try {
        const res = await api.get('/followups?status=open&limit=1');
        const s = res.data?.summary || {};
        setAlerts({ overdue: s.overdue_count || 0, dueToday: s.due_today_count || 0 });
      } catch { /* silently ignore */ }
    }

    fetchAlerts();
    const id = setInterval(fetchAlerts, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [role]);

  return alerts;
}

const ALL_NAV = [
  { items: [
    { label: 'Dashboard',     icon: LayoutDashboard, path: '/', roles: ['admin','manager','stockist','driver'] },
    { label: 'Rate Card',     icon: Tag,             path: '/rate-card', roles: ['admin','manager'] },
    { label: 'Rate Analysis', icon: TrendingUp,      path: '/rate-analysis', roles: ['admin','manager'] },
  ]},
  { title: 'TEAM', items: [
    { label: 'Calendar & Schedule', icon: Calendar,      path: '/calendar',  roles: ['admin','manager'], module: 'calendar' },
    { label: 'Team Checklist',      icon: CheckSquare,   path: '/checklist', roles: ['admin','manager'] },
    { label: 'Team Chat',           icon: MessageCircle, path: '/chat',      roles: ['admin','manager'] },
  ]},
  { title: 'STOCK', items: [
    { label: 'Live Stock',           icon: Package,      path: '/stock-view',      roles: ['admin','manager','stockist'] },
    { label: 'Sale Logs',            icon: ShoppingCart, path: '/logs/sales',      roles: ['admin','manager','driver'] },
    { label: 'Customer Performance', icon: BarChart2,    path: '/performance',     roles: ['admin','manager'], module: 'performance' },
    { label: 'Sale Calculator',      icon: Calculator,   path: '/sale-calculator', roles: ['admin','manager'], module: 'sale_calculator' },
    { label: 'Purchase Logs',        icon: ShoppingBag,  path: '/logs/purchases',  roles: ['admin','manager'] },
    { label: 'Payments Log',         icon: CreditCard,   path: '/payments-log',    roles: ['admin'] },
  ]},
  { title: 'ENTRY', items: [
    { label: 'Gas Purchase',            icon: Flame,          path: '/entry/purchase',     roles: ['admin','manager'] },
    { label: 'Gas Sale',                icon: ShoppingCart,   path: '/entry/sale',         roles: ['admin','manager','driver'] },
    { label: 'Godown Transfer',         icon: ArrowLeftRight, path: '/entry/transfer',     roles: ['admin','manager'] },
    { label: 'Adjustment / Defective',  icon: Wrench,         path: '/entry/adjustment',   roles: ['admin','manager','stockist'] },
    { label: 'Bulk Purchase (Bullet)',  icon: Database,       path: '/entry/bulk-purchase',roles: ['admin'] },
    { label: 'Refill from Bullet',      icon: RefreshCw,      path: '/entry/refill',       roles: ['admin','manager'] },
    { label: 'Payment Entry',           icon: DollarSign,     path: '/entry/payment',      roles: ['admin'] },
  ]},
  { title: 'EXPENSES', items: [
    { label: 'Expenses (Cash)', icon: Banknote, path: '/expenses', roles: ['admin','manager'] },
    { label: 'Diesel',          icon: Flame,    path: '/diesel',   roles: ['admin','manager','driver'] },
    { label: 'Vehicles',        icon: Truck,    path: '/vehicles', roles: ['admin','manager','driver'] },
  ]},
  { title: 'PENDING', items: [
    { label: 'MT Entry Pending',   icon: Clock,       path: '/pending/mt',       roles: ['admin','manager','stockist'], module: 'mt_tracking' },
    { label: 'Pending Payments',   icon: AlertCircle, path: '/pending/payments', roles: ['admin'] },
    { label: 'Pending Bill Entry', icon: FileText,    path: '/pending/bills',    roles: ['admin','manager'] },
    { label: 'Pending Cylinders',  icon: RotateCcw,   path: '/pending/cylinders',roles: ['admin','manager','stockist'] },
  ]},
  { title: 'PARTIES', items: [
    { label: 'Customers & Vendors', icon: Users,    path: '/parties',     roles: ['admin','manager'] },
    { label: 'Physical Register',   icon: BookOpen, path: '/register',    roles: ['admin','manager','stockist'] },
    { label: 'Payment Follow-ups',  icon: Phone,    path: '/followups',   roles: ['admin'] },
    { label: 'Order Predictions',   icon: Sparkles, path: '/predictions', roles: ['admin','manager'] },
  ]},
  { title: 'CHALLANS', items: [
    { label: 'Challans',     icon: FileSpreadsheet, path: '/challans',      roles: ['admin','manager','driver'] },
    { label: 'Challan Gaps', icon: SearchX,         path: '/challans/gaps', roles: ['admin','manager'] },
  ]},
  { title: 'REPORTS', items: [
    { label: 'Daily Tally',    icon: Scale,       path: '/tally',                 roles: ['admin','manager','stockist'] },
    { label: 'Stock Movement', icon: ArrowUpDown, path: '/stock-movement',        roles: ['admin','manager'], module: 'stock_movement' },
    { label: 'Adjustment Log', icon: History,     path: '/adjustments/log',       roles: ['admin','manager','stockist'] },
    { label: 'Daily Closing',  icon: Moon,        path: '/reports/daily-closing', roles: ['admin','manager'], module: 'daily_closing' },
    { label: 'Downloads',      icon: Download,    path: '/downloads',             roles: ['admin','manager'], module: 'downloads' },
  ]},
  { title: 'HR', items: [
    { label: 'Attendance',      icon: UserCheck,    path: '/attendance',         roles: ['admin','manager'], module: 'attendance' },
    { label: 'Monthly Summary', icon: CalendarDays, path: '/attendance/summary', roles: ['admin','manager'], module: 'attendance' },
    { label: 'Employees',       icon: Users,        path: '/employees',          roles: ['admin','manager'] },
  ]},
  { title: 'SETTINGS', items: [
    { label: 'Masters',           icon: Settings,  path: '/masters', roles: ['admin'], module: 'masters' },
    { label: 'Audit Logs',        icon: Shield,    path: '/audit',   roles: ['admin'] },
    { label: 'Sales & Marketing', icon: Megaphone, path: '/sales',   roles: ['admin','manager'] },
  ]},
];

const ROLE_BADGE = {
  admin:    { label: 'Admin',    bg: 'bg-white/20 text-white' },
  manager:  { label: 'Manager', bg: 'bg-white/15 text-gray-200' },
  stockist: { label: 'Stockist',bg: 'bg-white/15 text-gray-200' },
  driver:   { label: 'Driver',  bg: 'bg-white/15 text-gray-200' },
};

function filteredNav(role, enabledModules) {
  const mods = enabledModules || {};
  return ALL_NAV
    .map(s => ({ ...s, items: s.items.filter(i =>
      i.roles.includes(role) && (!i.module || mods[i.module] !== false)
    )}))
    .filter(s => s.items.length > 0);
}

function SidebarContent({ location, role, user, company, onClose, alerts }) {
  const enabledModules = company?.enabled_modules || {};
  const nav = filteredNav(role, enabledModules);
  const badge = ROLE_BADGE[role] || { label: role, bg: 'bg-white/15 text-gray-200' };

  /** Returns badge element for a nav path */
  function getNavBadge(path) {
    if (path === '/followups') {
      if (alerts.overdue > 0) {
        return (
          <span className="ml-auto flex items-center gap-0.5 flex-shrink-0">
            <span className="relative inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-[10px] font-bold text-white leading-none">
              {alerts.overdue > 99 ? '99+' : alerts.overdue}
              {/* pulse ring */}
              <span className="absolute inset-0 rounded-full bg-red-500 opacity-50 animate-ping" />
            </span>
          </span>
        );
      }
      if (alerts.dueToday > 0) {
        return (
          <span className="ml-auto flex-shrink-0">
            <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-[10px] font-bold text-white leading-none">
              {alerts.dueToday > 99 ? '99+' : alerts.dueToday}
            </span>
          </span>
        );
      }
    }
    return null;
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Logo + company */}
      <div className="px-4 py-3.5 border-b border-white/10 flex-shrink-0">
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-white rounded-sm flex items-center justify-center flex-shrink-0">
              <Flame size={14} className="text-black" strokeWidth={2.5} />
            </div>
            <span className="font-black text-white text-[13px] tracking-tight leading-none">VAYUSARTHI</span>
          </div>
          {onClose && (
            <button onClick={onClose} className="text-gray-500 hover:text-white lg:hidden">
              <X size={16} />
            </button>
          )}
        </div>
        {/* Company name */}
        <div className="text-[11px] text-gray-500 font-semibold truncate">{company?.name}</div>
        {/* User + role */}
        <div className="flex items-center gap-1.5 mt-1">
          <span className="text-[11px] text-gray-400 truncate flex-1">{user?.name}</span>
          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${badge.bg}`}>{badge.label}</span>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-1" style={{ scrollbarWidth: 'none' }}>
        {nav.map((section, si) => (
          <div key={si}>
            {section.title && (
              <div className="px-4 pt-3 pb-0.5 text-[10px] font-bold tracking-[0.12em] text-gray-600 uppercase select-none">
                {section.title}
              </div>
            )}
            {section.items.map(item => {
              const Icon = item.icon;
              const isActive =
                item.path === '/'
                  ? location.pathname === '/'
                  : location.pathname === item.path || location.pathname.startsWith(item.path + '/');
              const navBadge = getNavBadge(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={onClose}
                  className={[
                    'flex items-center gap-2.5 px-4 py-[7px] text-[12.5px] transition-colors',
                    isActive
                      ? 'bg-white/12 text-white font-medium'
                      : 'text-gray-400 hover:bg-white/8 hover:text-gray-200',
                  ].join(' ')}
                >
                  <Icon size={13} strokeWidth={isActive ? 2.2 : 1.8} className="flex-shrink-0" />
                  <span className="truncate">{item.label}</span>
                  {navBadge}
                </Link>
              );
            })}
          </div>
        ))}
        <div className="h-4" />
      </nav>
    </div>
  );
}

export default function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, company, role, companyActive, logout } = useAuth();
  const [notice, setNotice]         = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const alerts                       = useAlerts(role);

  const handleLogout = () => { logout(); navigate('/login'); };

  // Show subscription paused screen
  if (!companyActive) {
    return <SubscriptionPaused />;
  }

  return (
    <div className="flex h-screen overflow-hidden bg-white">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-[185px] bg-[#0a0a0a] flex-shrink-0 h-full overflow-hidden">
        <div className="flex-1 overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
          <SidebarContent location={location} role={role} user={user} company={company} onClose={null} alerts={alerts} />
        </div>
        <button
          onClick={handleLogout}
          className="flex items-center gap-2.5 px-4 py-3 text-[12.5px] text-gray-500 hover:text-white hover:bg-white/10 border-t border-white/10 flex-shrink-0 transition-colors"
        >
          <LogOut size={13} strokeWidth={1.8} />
          <span>Log out</span>
        </button>
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
          <aside className="relative z-10 flex flex-col w-56 bg-[#0a0a0a] h-full overflow-hidden">
            <div className="flex-1 overflow-y-auto">
              <SidebarContent location={location} role={role} user={user} company={company} onClose={() => setMobileOpen(false)} alerts={alerts} />
            </div>
            <button onClick={handleLogout}
              className="flex items-center gap-2.5 px-4 py-3 text-[12.5px] text-gray-500 hover:text-white border-t border-white/10">
              <LogOut size={13} /> <span>Log out</span>
            </button>
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Mobile topbar */}
        <div className="lg:hidden flex items-center gap-3 px-4 py-3 bg-[#0a0a0a] text-white flex-shrink-0">
          <button onClick={() => setMobileOpen(true)}><Menu size={20} /></button>
          <Flame size={16} strokeWidth={2.5} />
          <span className="font-black text-sm tracking-tight">VAYUSARTHI</span>
        </div>

        {/* Notification banner */}
        {notice && (
          <div className="flex items-center gap-3 px-5 py-2.5 bg-red-50 border-b border-red-100 flex-shrink-0">
            <BellOff size={14} className="text-red-500 flex-shrink-0" />
            <p className="text-[12.5px] text-red-700 flex-1 leading-snug">
              Notifications are blocked. Enable them in your browser settings to receive alerts.
            </p>
            <button onClick={() => setNotice(false)} className="text-red-400 hover:text-red-600 flex-shrink-0">
              <X size={14} />
            </button>
          </div>
        )}

        <main className="flex-1 overflow-y-auto bg-white">
          {children}
        </main>
      </div>
    </div>
  );
}
